import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import useHandleErrors from '../../utils/user.utils';
import { toast, ToastContainer } from 'react-toastify';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import Select from 'react-select';
import { useUser } from '../../utils/userContext';
import $ from 'jquery'; // Import jQuery for Bootstrap tooltip initialization

const ApproveTimesheet = () => {
    const [timesheets, setTimesheets] = useState([]);
    const [filteredTimesheets, setFilteredTimesheets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [action, setAction] = useState('');
    const [comment, setComment] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const handleErrors = useHandleErrors();
    const token = localStorage.getItem("token");
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRefs = useRef({});
    const hasFetchedRef = useRef(false);
    const { user } = useUser();
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [selectedTimesheet, setSelectedTimesheet] = useState(null);

    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchData();
            hasFetchedRef.current = true;
        }
    }, []);

    // Initialize Bootstrap tooltips
    useEffect(() => {
        // Initialize tooltips for elements with data-toggle="tooltip"
        $('[data-toggle="tooltip"]').tooltip();

        // Cleanup tooltips on component unmount
        return () => {
            $('[data-toggle="tooltip"]').tooltip('dispose');
        };
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const path = `/submitTimesheetDetails?emailId=${user?.OfficialEmail}`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 200) {
                const timesheetData = response.data.listTimesheetDetails.flatMap(item =>
                    item.timesheetSummary.map(summary => ({
                        ...summary,
                        employeeId: item.employeeId,
                        employeeName: item.employeeName || item.employeeId,
                        timeSheetHours: summary.timeSheetHours
                    }))
                );
                setTimesheets(timesheetData);
                setFilteredTimesheets(timesheetData);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    setTimesheets([]);
                    setFilteredTimesheets([]);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setFilteredTimesheets(
            timesheets.filter(timesheet =>
                timesheet.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                timesheet.employeeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                String(timesheet.weekNumber).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
        setCurrentPage(1);
    }, [searchQuery, timesheets]);

    const handleAction = async (timesheetId, action, weekStartDate, weekEndDate) => {
        if (!action) {
            toast.error("Please select an action (Approve/Reject).", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        if (action === 'Rejected' && !comment.trim()) {
            toast.error("Comment is required when rejecting a timesheet.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        setLoading(true);
        try {
            const path = `/approveTimesheet`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const payload = {
                EmployeeId: timesheetId,
                Status: action,
                Comments: comment,
                WeekStartDate: moment(weekStartDate, "YYYY-MM-DD").format("YYYY-MM-DD"),
                WeekEndDate: moment(weekEndDate, "YYYY-MM-DD").format("YYYY-MM-DD"),
            };
            const response = await axios.post(URL, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 200) {
                toast.success(`Timesheet ${action} Successfully!`, {
                    position: toast.POSITION.TOP_CENTER,
                });
                setAction('');
                setComment('');
                setOpenDropdown(null);
                fetchData();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error(`Error ${action.toLowerCase()} timesheet.`, {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const getUniqueCommentsByDate = (timeSheetHours) => {
        const commentMap = new Map();
        timeSheetHours.forEach(day => {
            day.projectHours.forEach(ph => {
                if (ph.userComments) {
                    commentMap.set(day.weekDate, ph.userComments);
                }
            });
        });
        return Array.from(commentMap.entries()).map(([date, comment]) => ({
            date,
            comment
        })).sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const renderTimesheetDetails = (timesheet) => {
        const dateHoursMap = {};
        const uniqueProjects = [];

        timesheet.timeSheetHours.forEach(timeSheetHour => {
            const formattedDate = timeSheetHour.weekDate;

            timeSheetHour.projectHours.forEach(projectHour => {
                if (!dateHoursMap[formattedDate]) {
                    dateHoursMap[formattedDate] = {};
                }
                if (!dateHoursMap[formattedDate][projectHour.projectName]) {
                    dateHoursMap[formattedDate][projectHour.projectName] = 0;
                }
                if (!uniqueProjects.includes(projectHour.projectName)) {
                    uniqueProjects.push(projectHour.projectName);
                }
            });
        });

        timesheet.timeSheetHours.forEach(timeSheetHour => {
            const formattedDate = timeSheetHour.weekDate;

            timeSheetHour.projectHours.forEach(projectHour => {
                dateHoursMap[formattedDate][projectHour.projectName] += projectHour.hours;
            });
        });

        // Adjust startDay to start from Monday (day 1)
        const startDay = moment(timesheet.weekStartDate, "YYYY-MM-DD").day(1).toDate();
        const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date(startDay);
            date.setDate(startDay.getDate() + i);
            const formattedDate = date.toISOString().split('T')[0];
            return formattedDate;
        });

        // Calculate total hours per day across all projects
        const dailyTotals = days.map(date => {
            const totalForDay = uniqueProjects.reduce((acc, projectName) => {
                return acc + ((dateHoursMap[date] && dateHoursMap[date][projectName]) || 0);
            }, 0);
            return totalForDay;
        });

        // Render table rows
        const projectRows = uniqueProjects.map((projectName, index) => {
            const daysData = days.map((date, i) => {
                const hoursForDate = (dateHoursMap[date] && dateHoursMap[date][projectName]) || 0;
                return (
                    <td key={i}>
                        {hoursForDate.toFixed(2)}
                    </td>
                );
            });

            return (
                <tr key={index}>
                    <td>{projectName}</td>
                    {daysData}
                    <th>
                        <div className="days-view-total-hours">
                            {timesheet.timeSheetHours
                                .filter(timeSheetHour => timeSheetHour.projectHours.some(projectHour => projectHour.projectName === projectName))
                                .reduce((acc, timeSheetHour) => acc + timeSheetHour.projectHours.reduce((acc, ph) => ph.projectName === projectName ? acc + ph.hours : acc, 0), 0).toFixed(2)
                            }
                        </div>
                    </th>
                </tr>
            );
        });

        // Add total row for daily hours
        const totalRow = (
            <tr key="total-row">
                <td><strong>Total</strong></td>
                {dailyTotals.map((total, i) => (
                    <td key={i}>
                        <strong>{total.toFixed(2)}</strong>
                    </td>
                ))}
                <th>
                    <div className="days-view-total-hours">
                        <strong>{dailyTotals.reduce((acc, total) => acc + total, 0).toFixed(2)}</strong>
                    </div>
                </th>
            </tr>
        );

        return [...projectRows, totalRow];
    };

    const sendEmail = (email) => {
        if (!timesheets.length) {
            toast.warning("No data available for export.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        const exportData = timesheets.map((timesheet) => {
            const uniqueComments = getUniqueCommentsByDate(timesheet.timeSheetHours);
            const comments = uniqueComments
                .map(({ date, comment }) => `${moment(date).format("DD-MM-YYYY")}: ${comment}`)
                .join("; ");
            return {
                "User ID": timesheet.employeeId,
                "Week Number": timesheet.weekNumber,
                "Start & End Date": `${timesheet.weekStartDate} - ${timesheet.weekEndDate}`,
                "Total (Hours)": timesheet.totalHours,
                Status: timesheet.status,
                Notes: comments,
            };
        });

        const ws = utils.json_to_sheet(exportData, {
            header: ["User ID", "Week Number", "Start & End Date", "Total (Hours)", "Status", "Notes"]
        });

        ws['!cols'] = [
            { wpx: 100 },
            { wpx: 100 },
            { wpx: 150 },
            { wpx: 100 },
            { wpx: 100 },
            { wpx: 300 },
        ];

        if (exportData.length > 0) {
            ['A1', 'B1', 'C1', 'D1', 'E1', 'F1'].forEach(cell => {
                if (ws[cell]) {
                    ws[cell].s = { font: { bold: true } };
                }
            });
        }

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Timesheets');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
            const link = document.createElement('a');
            link.href = reader.result;
            const today = new Date();
            const year = today.getFullYear().toString().slice(-2);
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const day = today.getDate().toString().padStart(2, '0');
            const formattedDate = `${year}${month}${day}`;
            link.download = `WW360-Approve_Timesheet_Report_${formattedDate}.xlsx`;

            link.click();

            const subject = "Timesheet List";
            const body = 'Please find the attached file.';
            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

            toast.info('The Excel file has been generated and downloaded. Please attach the file manually to your email.', {
                position: toast.POSITION.TOP_CENTER,
            });

            setTimeout(() => {
                window.location.href = mailtoLink;
            }, 3000);
        };
    };

    const handleExport = () => {
        if (!timesheets.length) {
            toast.warning("No data available for export.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        const allDates = Array.from(new Set(timesheets.flatMap(timesheet =>
            timesheet.timeSheetHours.map(day => moment(day.weekDate, "YYYY-MM-DD").format("DD-MM-YYYY"))
        )));

        const headers = ["Employee ID", "Employee Name", "Week Number", "Start & End Date", "Total (Hours)", "Status", "Dates", "Hours", "Projects", "Notes"];

        const exportData = timesheets.flatMap(timesheet => {
            const uniqueComments = getUniqueCommentsByDate(timesheet.timeSheetHours);
            const commentMap = new Map(uniqueComments.map(({ date, comment }) => [moment(date).format("DD-MM-YYYY"), comment]));

            return allDates.flatMap(date => {
                const dayData = timesheet.timeSheetHours.find(day =>
                    moment(day.weekDate, "YYYY-MM-DD").format("DD-MM-YYYY") === date
                );

                if (!dayData || !dayData.projectHours.length) {
                    return [];
                }

                return dayData.projectHours.map(project => ({
                                    "Employee ID": timesheet.employeeId,
                                    "Employee Name": timesheet.employeeName,
                                    "Week Number": timesheet.weekNumber,
                                    "Start & End Date": `${timesheet.weekStartDate} - ${timesheet.weekEndDate}`,
                                    "Total (Hours)": timesheet.totalHours,
                                    "Status": timesheet.status,
                                    // Parse date string (DD-MM-YYYY) into a real Date using moment
                                    "Dates": moment(date, "DD-MM-YYYY").toDate(),
                                    // Add weekday short name (e.g., Mon, Tue)
                                    "Day": moment(date, "DD-MM-YYYY").format("ddd"),
                                    "Hours": project.hours,
                                    "Projects": project.projectName,
                                    "Notes": commentMap.get(date) || ""
                                }));
                            }).filter(row => row.length !== 0);
        });

        // Create a worksheet from the transformed data
                const ws = utils.json_to_sheet(exportData, { header: headers });
        
                ws['!cols'] = headers.map(() => ({ wpx: 120 }));
        
                if (exportData.length > 0) {
                    // Header styling for columns A..K (now includes Day as column H)
                    ['A1','B1','C1','D1','E1','F1','G1','H1','I1','J1','K1'].forEach(cell => {
                        if (ws[cell]) {
                            ws[cell].s = { font: { bold: true } };
                        }
                    });
                }
        

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Timesheet');

        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array', cellDates: true, dateNF: 'dd-mm-yyyy' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2);
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const day = today.getDate().toString().padStart(2, '0');
        const formattedDate = `${year}${month}${day}`;
        saveAs(data, `WW360-Approve_Timesheet_Report_${formattedDate}.xlsx`);
    };

    const shareData = {
        title: 'Approve Timesheet List',
        text: 'Check out the Approve Timesheet List',
        url: window.location.href
    };

    const handleShare = async () => {
        try {
            await navigator.share(shareData);
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const options = [
        { value: 'Approved', label: 'Approve' },
        { value: 'Rejected', label: 'Reject' },
    ];

    const ClearIndicator = () => null;

    const handleRecordsPerPageChange = (event) => {
        setRecordsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredTimesheets.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredTimesheets.length / recordsPerPage);

    const paginate = pageNumber => setCurrentPage(pageNumber);

    const handleDropdownToggle = (employeeId) => {
        if (openDropdown === employeeId) {
            setOpenDropdown(null);
            setAction('');
            setComment('');
        } else {
            setOpenDropdown(employeeId);
            setAction('');
            setComment('');
            if (openAccordion === employeeId) {
                setOpenAccordion(null);
            }
        }
    };

    const handleOutsideClick = (event) => {
        if (Object.values(dropdownRefs.current).every(ref => ref && !ref.contains(event.target))) {
            setOpenDropdown(null);
            setAction('');
            setComment('');
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    const [openAccordion, setOpenAccordion] = useState(null);

    const handleAccordionToggle = (id) => {
        setOpenAccordion(openAccordion === id ? null : id);
    };

    const handleNotesClick = (timesheet) => {
        setSelectedTimesheet(timesheet);
        setShowNotesModal(true);
    };

    const closeNotesModal = () => {
        setShowNotesModal(false);
        setSelectedTimesheet(null);
    };

    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="row mb-2">
                                <div className="col">
                                    <h3 className="title mb-2">Approve Timesheet</h3>
                                </div>
                                <div className="col-auto">
                                    <a href="/SubmitLeave" className="btn btn-primary mt-lg-0 mt-4">
                                        <i className="ri-calendar-schedule-line"></i> Apply Leave
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    {timesheets?.length === 0 ? (
                        <div className="container">
                            <div className="row vh-50 justify-content-center align-items-center">
                                <div className="col-auto">
                                    <div className="no-data-wrapper">
                                        <div className="no-data-found">
                                            <i className="ri-file-damage-fill"></i>
                                            <div className="bounce"><i className="ri-search-eye-line"></i></div>
                                            <p>No Data Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="row align-items-center justify-content-center">
                                <div className="col-5">
                                    <div className="container-fluid row-rounded mb-0">
                                        <div className="row px-2 py-0">
                                            <div className="input-group mb-0">
                                                <input
                                                    type="search"
                                                    className="form-control search-input"
                                                    placeholder="Keyword search..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                <div className="input-group-append">
                                                    <button className="btn btn-primary" type="button" title="Search" data-toggle="tooltip">
                                                        <i className="ri-menu-search-line"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <small className="form-text text-muted text-center mt-0">
                                        <i className="ri-information-2-fill text-info"></i> Allows: User ID - Employee Name - Week Number
                                    </small>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col">
                                                    <h5 className="title mt-1 mb-0">Summary</h5>
                                                </div>
                                                <div className="col-auto">
                                                    <button type="button" title="Email" className="btn btn-light btn-circle ml-2" onClick={() => sendEmail('')} data-toggle="tooltip">
                                                        <i className="ri-mail-send-fill"></i>
                                                    </button>
                                                    <button type="button" title="Share" className="btn btn-light btn-circle ml-2" onClick={handleShare} data-toggle="tooltip">
                                                        <i className="ri-share-fill"></i>
                                                    </button>
                                                    <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={handleExport} data-toggle="tooltip">
                                                        <i className="ri-export-fill"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="row mt-3">
                                                <div className="col-md-12">
                                                    <div className="table-responsive timesheet-information">
                                                        <table className="table grid">
                                                            <thead>
                                                                <tr>
                                                                    <th>S.No.</th>
                                                                    <th>Emp ID</th>
                                                                    <th>Emp Name</th>
                                                                    <th>Manager Name</th>
                                                                    <th>Week Number</th>
                                                                    <th>Start & End Date</th>
                                                                    <th>Total <small>(Hours)</small></th>
                                                                    <th></th>
                                                                    <th></th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {currentRecords.map((timesheet, timesheetIndex) => {
                                                                    const uniqueId = `${timesheet.employeeId}-${timesheet.weekNumber}`;
                                                                    return (
                                                                        <React.Fragment key={uniqueId}>
                                                                            <tr>
                                                                                <td>{indexOfFirstRecord + timesheetIndex + 1}</td>
                                                                                <td>{timesheet.employeeId}</td>
                                                                                <td>{timesheet.employeeName}</td>
                                                                                <td>{timesheet.managerName || "N/A"}</td>
                                                                                <td className="text-center">
                                                                                    <span className="text-info border badge border-info">{timesheet.weekNumber}</span>
                                                                                </td>
                                                                                <td>{moment(timesheet.weekStartDate).format("DD-MM-YYYY")} - {moment(timesheet.weekEndDate).format("DD-MM-YYYY")}</td>
                                                                                <td className="text-center">{timesheet.totalHours}</td>
                                                                                <td className="text-center">
                                                                                    <button
                                                                                        className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                                        onClick={() => handleNotesClick(timesheet)}
                                                                                        data-toggle="tooltip"
                                                                                        title="View Notes"
                                                                                    >
                                                                                        <i className="ri-sticky-note-fill"></i>
                                                                                    </button>
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <button
                                                                                        className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                                        onClick={() => handleAccordionToggle(uniqueId)}
                                                                                        data-toggle="tooltip"
                                                                                        title="View Details"
                                                                                    >
                                                                                        <i className="ri-information-line"></i>
                                                                                    </button>
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <div className="dropdown" ref={el => dropdownRefs.current[uniqueId] = el}>
                                                                                        <button
                                                                                            className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                                            type="button"
                                                                                            onClick={() => handleDropdownToggle(uniqueId)}
                                                                                            data-toggle="tooltip"
                                                                                            title="Approve/Reject"
                                                                                        >
                                                                                            <i className="ri-list-settings-line"></i>
                                                                                        </button>
                                                                                        {openDropdown === uniqueId && (
                                                                                            <div className="dropdown-menu custom-dropdown shadow show">
                                                                                                <div>
                                                                                                    <div className="form-group">
                                                                                                        <label htmlFor={`action${uniqueId}`} className="required">Action</label>
                                                                                                        <Select
                                                                                                            className="react-select-container"
                                                                                                            classNamePrefix="react-select"
                                                                                                            options={options}
                                                                                                            value={options.find(opt => opt.value === action)}
                                                                                                            onChange={(selectedOption) => setAction(selectedOption ? selectedOption.value : '')}
                                                                                                            isClearable
                                                                                                            menuPortalTarget={document.body}
                                                                                                            components={{ ClearIndicator }}
                                                                                                            styles={{
                                                                                                                menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                                                                                control: base => ({ ...base, zIndex: 1 })
                                                                                                            }}
                                                                                                        />
                                                                                                    </div>
                                                                                                    <div className="form-group">
                                                                                                        <label htmlFor={`comment${uniqueId}`} className="required">Comment <small>(If Reject)</small></label>
                                                                                                        <textarea
                                                                                                            className="form-control"
                                                                                                            rows="3"
                                                                                                            id={`comment${uniqueId}`}
                                                                                                            value={comment}
                                                                                                            onChange={(e) => setComment(e.target.value)}
                                                                                                        ></textarea>
                                                                                                    </div>
                                                                                                    <div className="text-center">
                                                                                                        <button type="button" className="btn btn-light" onClick={() => handleDropdownToggle(uniqueId)}>Cancel</button>
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="btn btn-primary"
                                                                                                            onClick={() => handleAction(timesheet.employeeId, action, timesheet.weekStartDate, timesheet.weekEndDate)}
                                                                                                        >
                                                                                                            Submit <i className="ri-checkbox-circle-line"></i>
                                                                                                        </button>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                            {openAccordion === uniqueId && (
                                                                                <tr className="accordion">
                                                                                    <td colSpan="10" className="px-0">
                                                                                        <div className="accordion-content days-view section-submitted">
                                                                                            <table className="table grid">
                                                                                                <thead className="bg-light">
                                                                                                    <tr>
                                                                                                        <th>Activity</th>
                                                                                                        <th>Sun <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(0).format('DD-MM-YYYY')}</small></th>
                                                                                                        <th>Mon <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(1).format('DD-MM-YYYY')}</small></th>
                                                                                                        <th>Tue <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(2).format('DD-MM-YYYY')}</small></th>
                                                                                                        <th>Wed <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(3).format('DD-MM-YYYY')}</small></th>
                                                                                                        <th>Thu <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(4).format('DD-MM-YYYY')}</small></th>
                                                                                                        <th>Fri <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(5).format('DD-MM-YYYY')}</small></th>
                                                                                                        <th>Sat <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(6).format('DD-MM-YYYY')}</small></th>
                                                                                                        <th>Total <small>(Hours)</small></th>
                                                                                                    </tr>
                                                                                                </thead>
                                                                                                <tbody>
                                                                                                    {renderTimesheetDetails(timesheet)}
                                                                                                </tbody>
                                                                                            </table>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            )}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                <div className="col-md-12">
                                                    <div className="container-fluid">
                                                        <div className="row border-top pt-3">
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Records</p>
                                                            </div>
                                                            <div className="col-1 record-drop">
                                                                <select className="form-control" value={recordsPerPage} onChange={handleRecordsPerPageChange}>
                                                                    <option value="5">5</option>
                                                                    <option value="10">10</option>
                                                                    <option value="15">15</option>
                                                                    <option value="20">20</option>
                                                                    <option value="25">25</option>
                                                                </select>
                                                            </div>
                                                            <div className="col">
                                                                <nav>
                                                                    <ul className="pagination justify-content-center">
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-double-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        {Array.from({ length: totalPages }, (_, index) => (
                                                                            <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                                    {index + 1}
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                                                <i className="ri-arrow-right-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(totalPages)}>
                                                                                <i className="ri-arrow-right-double-fill"></i>
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            </div>
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showNotesModal && selectedTimesheet && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Notes for {moment(selectedTimesheet.weekStartDate, "YYYY-MM-DD").format("DD-MM-YYYY")} to {moment(selectedTimesheet.weekEndDate, "YYYY-MM-DD").format("DD-MM-YYYY")}</h5>
                                <button type="button" className="close" onClick={closeNotesModal}>
                                    <span>×</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                {selectedTimesheet.timeSheetHours.some(day => day.projectHours.some(ph => ph.userComments)) ? (
                                    <ul className="list-group">
                                        {getUniqueCommentsByDate(selectedTimesheet.timeSheetHours).map((comment, idx) => (
                                            <li key={idx} className="list-group-item text-left">
                                                <strong>{moment(comment.date).format("DD-MM-YYYY")}</strong> - {comment.comment}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-center">No comments available for this timesheet.</p>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeNotesModal}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApproveTimesheet;